import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { auth } from "@/lib/api";
import {
  INACTIVITY_MS,
  chiediSessioneAllApritore,
  clearToken,
  idleFor,
  isTemporary,
  readToken,
  rispondiAlleRichiesteDiSessione,
  saveToken,
  setSignOutReason,
  touch,
} from "@/lib/session";

const AuthCtx = createContext(null);

// How often the watchdog looks at the clock. Short enough that the session does
// not outlive the limit by much, long enough to cost nothing.
const CHECK_MS = 30 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    /*
      Una scheda aperta da un'altra eredita la sessione.

      «Vedi nel testo» apre l'editor in una seconda scheda: senza questo,
      quella scheda nasceva senza credenziali - la sessione non ricordata vive
      in sessionStorage, che è per scheda - e mostrava la pagina di accesso al
      posto del passaggio che si voleva leggere.
    */
    if (!readToken() && window.opener) {
      const prestato = await chiediSessioneAllApritore();
      if (prestato) saveToken(prestato, false);
    }
    /*
      Senza una sessione non si chiede niente a nessuno.

      Prima l'applicazione domandava comunque al server «chi sono?», riceveva
      un 401 e solo allora mostrava la pagina di accesso. Su un server sveglio
      sono trenta millisecondi; su un server che si sta riaccendendo - e
      l'hosting gratuito spegne quello di NUMA dopo un quarto d'ora di
      silenzio - è un minuto intero di logo che gira davanti a chi voleva solo
      accedere. Chi non ha una sessione va alla pagina di accesso subito: il
      server lo sveglierà premendo «Accedi».
    */
    if (!readToken()) {
      setUser(null);
      setLoading(false);
      return;
    }

    /*
      Con una sessione, invece, si aspetta - ma per un tempo dichiarato.

      La prima richiesta a un server addormentato non risponde per una
      quarantina di secondi e poi funziona: rinunciare al primo errore
      manderebbe alla pagina di accesso chi è già dentro. Si riprova due volte,
      distanziando i tentativi; se è la sessione a non valere più - il server
      risponde, e risponde di no - non si insiste.
    */
    for (let tentativo = 0; tentativo < 3; tentativo += 1) {
      try {
        const u = await auth.me();
        setUser(u);
        setLoading(false);
        return;
      } catch (err) {
        const stato = err?.response?.status;
        const serverRisponde = Boolean(stato) && stato !== 502 && stato !== 503 && stato !== 504;
        if (serverRisponde || tentativo === 2) break;
        await new Promise((r) => setTimeout(r, 2500 * (tentativo + 1)));
      }
    }
    setUser(null);
    setLoading(false);
  }, []);

  // Ogni scheda è anche potenziale prestatrice per quelle che aprirà.
  useEffect(() => rispondiAlleRichiesteDiSessione(), []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (data) => {
    const { remember = false, ...credentials } = data;
    const res = await auth.login({ ...credentials, remember });
    saveToken(res.token, remember);
    setUser(res.user);
    return res.user;
  };
  const register = async (data) => {
    const res = await auth.register(data);
    // A new account is on the machine its owner just chose to type a password
    // into, and the first thing it does is send them to their mailbox and back.
    saveToken(res.token, true);
    setUser(res.user);
    return res.user;
  };

  const signOut = useCallback(async (reason = "") => {
    try { await auth.logout(); } catch { /* ignore */ }
    clearToken();
    if (reason) setSignOutReason(reason);
    setUser(null);
  }, []);

  const logout = useCallback(() => signOut(""), [signOut]);

  /*
    The inactivity watchdog, for sessions that were not asked to be remembered.

    It runs on the wall clock rather than on a timeout, so a laptop that was
    shut and reopened an hour later comes back already signed out - a timer
    alone would have been suspended along with the machine.
  */
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  useEffect(() => {
    if (!user || !isTemporary()) return undefined;

    touch();
    const seen = () => touch();
    const events = ["mousedown", "keydown", "wheel", "touchstart", "scroll"];
    events.forEach((name) => window.addEventListener(name, seen, { passive: true }));

    const check = () => {
      if (idleFor() > INACTIVITY_MS) signOutRef.current("inattivita");
    };
    const onReturn = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onReturn);
    const timer = setInterval(check, CHECK_MS);
    check();

    return () => {
      events.forEach((name) => window.removeEventListener(name, seen));
      document.removeEventListener("visibilitychange", onReturn);
      clearInterval(timer);
    };
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
